import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { resetUser, trackEvent } from "../mixpanel";
import { MIXPANEL_EVENTS } from "../mixpanel/events";

export type LogoutReason = "manual" | "session_expired";
export type LogoutHandler = (reason: LogoutReason) => void;

let activeHandler: LogoutHandler | null = null;

/**
 * Installs the single active logout handler. Returns an uninstaller for the
 * useEffect cleanup. The "single slot" semantic is deliberate: there is
 * exactly one AuthLogoutListener mounted at a time, and it owns the React-
 * tree dependencies (navigate, setIsAuthenticated) needed to execute logout.
 * The uninstall guard (handler-identity check) is intentionally StrictMode-
 * safe: React invokes the cleanup with the same closure it registered, so
 * the double-mount in dev does not clear an already-replaced handler.
 */
export function setLogoutHandler(handler: LogoutHandler): () => void {
  activeHandler = handler;
  return () => {
    if (activeHandler === handler) activeHandler = null;
  };
}

/**
 * Triggers the logout flow. Called from React (Header, Settings) and from
 * non-React contexts (apiClient interceptors on 401). If the React tree has
 * not yet mounted (boot-time race where a stale token causes an immediate
 * 401 before AuthLogoutListener renders), falls back to a hard redirect.
 *
 * Re-entrancy is guarded INSIDE performLogout, not here, so the no-handler
 * fallback path is not affected by an in-flight regular logout.
 */
export function triggerLogout(reason: LogoutReason): void {
  if (activeHandler) {
    activeHandler(reason);
    return;
  }
  // React tree not mounted; hard-redirect fallback.
  try { Sentry.setUser(null); } catch { /* ignore — Sentry may not be initialized yet */ }
  try { resetUser(); } catch { /* ignore — Mixpanel may not be initialized yet */ }
  localStorage.removeItem("userAccessToken");
  localStorage.removeItem("selectedMode");
  window.location.replace("/signin");
}

interface LogoutDeps {
  navigate: (path: string) => void;
  setIsAuthenticated: (v: boolean) => void;
}

let inFlight = false;
let resetTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Executes the logout side-effects with injected dependencies — all React-tree
 * deps are passed in so the function is unit-testable without React.
 *
 * Re-entrancy guard: a `session_expired` flight will swallow concurrent
 * `session_expired` reentries (the 401-storm dedup case). A manual logout
 * always proceeds — it is user-initiated and we never want to ignore it.
 *
 * The `inFlight` flag is released on a `setTimeout(0)` macrotask boundary
 * rather than synchronously in `finally`. Two independent axios interceptors
 * (apiClient.ts, apiClient2.ts) each getting a 401 around the same time call
 * this from separate, non-nested microtasks — they never overlap on the call
 * stack, so a synchronous reset would clear the guard before the second
 * interceptor's rejection handler runs, and the dedup would silently not
 * fire. All currently-queued microtasks drain before any setTimeout macrotask
 * fires, so keeping `inFlight = true` until that boundary correctly dedups
 * same-tick concurrent 401s while still resetting quickly enough for a
 * genuinely later session's logout to work normally. IMPORTANT: the
 * implementation is otherwise synchronous end-to-end. If a future change
 * introduces `await` inside this function, the re-entrancy guard must be
 * re-evaluated — async interleaving would break it.
 */
export function performLogout(reason: LogoutReason, deps: LogoutDeps): void {
  if (reason === "session_expired" && inFlight) return;
  if (resetTimer !== undefined) {
    clearTimeout(resetTimer);
    resetTimer = undefined;
  }
  inFlight = true;
  try {
    // Third-party SDKs — wrap because they can throw.
    try {
      Sentry.setUser(null);
    } catch (err) {
      console.error("Sentry.setUser(null) failed", err);
    }
    try {
      resetUser();
    } catch (err) {
      console.error("Mixpanel resetUser failed", err);
    }

    // First-party operations — cannot throw under normal conditions; no catch.
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("selectedMode");
    deps.setIsAuthenticated(false);

    if (reason === "manual") {
      try {
        trackEvent(MIXPANEL_EVENTS.LOGOUT_INITIATED);
      } catch (err) {
        console.error("Mixpanel trackEvent failed", err);
      }
      toast.info("You've been signed out.", { toastId: "logout-manual" });
    } else {
      toast.info("Your session has expired. Please sign in again.", { toastId: "session-expired" });
    }

    try {
      deps.navigate("/signin");
    } catch (err) {
      console.error("react-router navigate failed; falling back to location.replace", err);
      window.location.replace("/signin");
    }
  } finally {
    // Manual logout resets quickly so a subsequent session works normally.
    // Session-expired uses a longer window to absorb staggered 401s from
    // multiple in-flight requests that all expire around the same time.
    resetTimer = setTimeout(() => {
      inFlight = false;
    }, reason === "session_expired" ? 10_000 : 0);
  }
}

/** Test-only — resets module-level state between cases. */
export function __resetForTests(): void {
  activeHandler = null;
  inFlight = false;
  if (resetTimer !== undefined) {
    clearTimeout(resetTimer);
    resetTimer = undefined;
  }
}
